import styles from "@/styles/checkout.module.scss"
import { Button, Checkbox, Input, ConfigProvider, message, Form } from "antd"
import Link from "next/link.js"
import { useRef, useState, useEffect } from "react"
import loginApi from "@/service/modules/loginApi"
import AddressForm from "./AddressForm"
import { useGoogleLoginUtils } from '@/utils/googleLogin'; // 替换为实际的路径

import getConfig from 'next/config'
import { WarningOutlined } from "@ant-design/icons"
const { publicRuntimeConfig } = getConfig()
const imageDomain = publicRuntimeConfig.imageDomain
import GoogleIcon from '@/svgs/checkout/google.svg'

// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';
import { matomoSetCustomDimension } from "@/utils/matomoEvent"

interface Props {
  changeLoginStatus: Function
}

export default function AuthenticatedCheckout({ changeLoginStatus }: Props) {
  const [form] = Form.useForm();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [email, setEmail] = useState('')
  const [hasUserInfo, setHasUserInfo] = useState(-1)
  const [showPasswordInput, setShowPasswordInput] = useState(false)
  const [showSignin, setShowSignin] = useState(true)
  const [subscriptionEmail, setSubscriptionEmail] = useState(false)
  const [canEdit, setCanEdit] = useState(false)
  const [showErrorBox, setShowErrorBox] = useState(false)

  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');

  // 定义防抖函数的延迟时间（以毫秒为单位）
  const DEBOUNCE_DELAY = 300;
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const emailReg = /\w+([-+.]\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*/
  // 绑定email地址
  const emailInputChange = (e: any) => {
    const { value } = e.target
    setEmail(value)
    // 在调用checkEmail之前，先清除之前的定时器
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (emailReg.test(value)) {
      // 设置新的定时器
      debounceTimeoutRef.current = setTimeout(() => {
        checkEmail(value);
      }, DEBOUNCE_DELAY);
    }
  }

  const checkEmail = async (email: string) => {
    try {
      const response = await loginApi.checkEmail(email)
      const { exist } = response.data
      if (exist == 0) {
        // 未注册过
        setSubscriptionEmail(true)
        setHasUserInfo(0)
        setShowPasswordInput(false)
        setShowSignin(false)
        setCanEdit(true)
      } else if (exist == 1) {
        // 已有账号
        setShowPasswordInput(true)
        setHasUserInfo(1)
        setShowSignin(false)
        setCanEdit(false)
      }

    } catch (error: any) {
      console.log(error);
    }
  }

  useEffect(() => {
    if (hasUserInfo < 1) {
      form.setFieldsValue({
        password: ''
      })
    }
  }, [hasUserInfo])

  const successAddress = (addressID: number | string) => {
    changeLoginStatus(true)
  }

  // 获取信用卡支付错误次数以及Recaptcha key
  const getRecaptchaSiteKey = async () => {
    try {
      let res = await loginApi.LoginErrorCheck()
      if (res && res.data) {
        setRecaptchaSiteKey(res.data.siteKey || '')
      }
    } catch (error: any) {
      message.error(error.message);
      console.log(error.response);
    }
  }

  // 获取Recaptcha token
  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaToken(value || '')
  };

  useEffect(() => {
    getRecaptchaSiteKey()
  }, [])

  const login = async (values: any) => {
    try {

      const { email, password } = values
      const gRecaptchaResponse = recaptchaRef.current?.getValue();
      if (recaptchaSiteKey && !gRecaptchaResponse) {
        message.error('Please verify you are not a robot')
        return
      }
      const params = {
        email: email.trim(),
        password: window.btoa(password),
        recaptcha: ''
      }

      if (recaptchaSiteKey) {
        params.recaptcha = gRecaptchaResponse
      }

      const finnalParams = Object.fromEntries(
        Object.entries(params).filter(([key, value]) => value !== null && value !== '')
      );
      let res = await loginApi.login(finnalParams)

      if (res.data) {
        changeLoginStatus(true)

        // matomo记录登录结果
        let loginResult = 0
        if (window && (window as any)._paq) {
          (window as any)._paq.push(['setUserId', res.data.customerID]);
        }
        matomoSetCustomDimension(26, {loginMethod: 'chkLogin', loginResult})
      }
    } catch (error: any) {
      setShowErrorBox(true)
      // message.error(error.message)
      if (error.code == 115003) {
        alert(error.message)
      }

      // 刷新验证码
      if (
        error.code == 115004 || error.code == 110000) {
        if (recaptchaSiteKey) {
          recaptchaRef.current.reset();
        } else {
          getRecaptchaSiteKey()
        }
      }

      // matomo 记录登录失败信息
      const loginResult = error.code
      matomoSetCustomDimension(26, {loginMethod: 'chkLogin', loginResult})
    }
  }

  const useNewEmail = () => {
    setShowSignin(true)
    setShowPasswordInput(false)
    setShowErrorBox(false)
  }

  const onSuccessJump = () => {
    changeLoginStatus(true)
  }

  // Google登录
  const onGoogleLogin = useGoogleLoginUtils({ onSuccessJump, source: 'checkout' });

  return (
    <div className={styles["checkout-user-info"]}>
      <div className={styles["checkout-step"]}>
        <div className={styles["checkout-step-item"]}>
          <div className={styles["stepTitle"]}>
            1.Your Email{" "}
            <span className={styles["desc"]}>
              (to send your order comfirmation)*
            </span>
          </div>
          <Form
            form={form}
            name="login-form"
            onFinish={login}
            initialValues={{ remember: true }}
            autoComplete="off"
          >
            <div className={styles["login-box"]}>
              <div className={styles["login-item"]}>
                <div className={styles["login-item-title"]}>
                  Enter your email
                </div>
                <div className={styles["login-item-input"]}>
                  <Form.Item
                    name="email"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a valid email!",
                      },
                      { type: "email", message: "Please enter a valid email!" },
                    ]}
                  >
                    <Input
                      name="email"
                      placeholder="Enter your email"
                      onChange={emailInputChange}
                    />
                  </Form.Item>
                </div>
              </div>
              <div
                className={styles["login-item"]}
                style={{ display: showPasswordInput ? "block" : "none" }}
              >
                <div className={styles["login-item-title"]}>
                  <span style={{ color: "#D70B17", marginRight: "4px" }}>
                    *
                  </span>
                  Enter password for faster checkout
                  <Link href="/reset-password" legacyBehavior>
                    <a rel="noopener noreferrer" className={styles["ask"]}>
                      Forgot Password?
                    </a>
                  </Link>
                </div>
                <div className={styles["login-item-input"]}>
                  <Form.Item
                    name="password"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a valid password!",
                      },
                    ]}
                  >
                    <Input
                      name="password"
                      type="password"
                      placeholder="Enter your password"
                    />
                  </Form.Item>
                </div>
              </div>
              {!!(!showPasswordInput && showSignin) && (
                <div className={styles["login-item"]}>
                  <div className={styles["login-item-title"]}>
                    Already have an account?
                  </div>
                  <div className={styles["login-item-input"]}>
                    <Button
                      type="primary"
                      onClick={() => {
                        setShowPasswordInput(true);
                        setShowSignin(false);
                      }}
                      style={{ fontWeight: "bold" }}
                    >
                      Sign In
                    </Button>
                  </div>
                </div>
              )}
            </div>
            {showPasswordInput && (
              <>
                {!!recaptchaSiteKey && (
                  <div className={styles["recaptcha-box"]}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={recaptchaSiteKey}
                      type={"audio"}
                      onChange={handleRecaptchaChange}
                    />
                  </div>
                )}
                <div className={styles["login-btn"]}>
                  <Button
                    type="primary"
                    style={{ width: "100%", fontWeight: "bold" }}
                    htmlType="submit"
                  >
                    LOGIN
                  </Button>
                </div>
              </>
            )}
          </Form>
          {hasUserInfo == 0 && (
            <div className={styles["subscription-btn"]}>
              <Checkbox
                checked={subscriptionEmail}
                onChange={(e) => {
                  setSubscriptionEmail(e.target.checked);
                }}
              >
                keep me up to date on news and exlusive offers
              </Checkbox>
            </div>
          )}

          {showErrorBox && (
            <div className={styles["login-error-box"]}>
              <WarningOutlined className={styles["warningOutLine"]} />
              <div className={styles["error-words"]}>
                Email and/or password are incorrect.
              </div>
              <Button
                type="link"
                href="/reset-password"
                className={styles["error-link"]}
              >
                Forgot password?
              </Button>
              <span className={styles["error-or"]}>or</span>
              <Button
                type="link"
                className={styles["error-link"]}
                onClick={useNewEmail}
              >
                Use new email
              </Button>
            </div>
          )}

          <div
            className={styles["google-login"]}
            onClick={() => onGoogleLogin()}
          >
            <div className={styles["google-login-line"]}></div>
            <div className={styles["google-login-content"]}>
              <span>OR SIGN IN WITH</span>
              <GoogleIcon style={{ width: "24px" }} />
            </div>
            <div className={styles["google-login-line"]}></div>
          </div>
        </div>

        <div className={styles["checkout-step-item"]}>
          <div className={styles["stepTitle"]}>2.Your Address</div>
          {/* <ConfigProvider componentDisabled={!canEdit}> */}
          <ConfigProvider>
            <AddressForm
              address={null}
              success={successAddress}
              cancel={undefined}
              email={email}
              subscriptionEmail={subscriptionEmail}
              type="add"
              canEdit={canEdit}
            />
          </ConfigProvider>
        </div>
      </div>
    </div>
  );
}